import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router';
import { BRAND } from '@shared/constants/brand';
import { countMenus } from '@shared/data/menu';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const FAQ: Array<{ q: string; a: string }> = [
  {
    q: '회원가입이 필요한가요?',
    a: '아니요. 방을 만들 때 닉네임만 입력하면 됩니다. 이메일, 전화번호, 비밀번호 같은 계정 정보는 받지 않습니다.',
  },
  {
    q: '왜 선택 순서를 미리 알려주지 않나요?',
    a: '순서가 공개되면 "다음은 내 차례니까 미리 정해둬야지" 하는 눈치 싸움이 생깁니다. 현재 선택하는 사람만 공개하고 다음 사람은 비밀로 두면 모두가 결과를 기다리는 재미가 생기고, 누가 무엇을 골랐는지는 끝난 뒤 한 번에 보여줍니다.',
  },
  {
    q: '4단계가 뭔가요?',
    a: '1단계 배달/외식, 2단계 음식 종류(한식·중식·일식 등), 3단계 세부 종류(찌개/탕, 돈카츠 등), 4단계 최종 메뉴입니다. 각 단계를 서로 다른 사람이 돌아가며 고릅니다.',
  },
  {
    q: '3단계에서 끝나는 경우도 있던데요?',
    a: '만두/딤섬, 돈카츠, 파스타처럼 세부 종류만 정해지면 갈 가게가 정해지는 경우에는 3단계에서 끝납니다. 가게에 가서 각자 먹고 싶은 메뉴를 고르면 되니까요. 찌개/탕처럼 메뉴에 따라 가게가 달라지는 경우에만 4단계까지 갑니다.',
  },
  {
    q: '참여 인원이 4명보다 많거나 적으면요?',
    a: '2명이면 번갈아 두 번씩, 3명이면 첫 사람이 1단계와 4단계를 맡습니다. 5명 이상이면 한 게임에서 최대 4명이 선택하고, 다시하기를 누르면 순서를 새로 뽑습니다.',
  },
  {
    q: '고르기 귀찮으면요?',
    a: '모든 단계에 랜덤 버튼이 있습니다. 결과는 서버에서 정해지기 때문에 모든 참여자에게 같은 결과가 보입니다.',
  },
  {
    q: '실수로 새로고침하거나 연결이 끊기면요?',
    a: '같은 브라우저에서 다시 열면 기존 닉네임으로 자동 복귀합니다. 선택 차례인 사람의 연결이 끊기면 30초를 기다린 뒤 서버가 대신 랜덤으로 골라 게임이 멈추지 않게 합니다.',
  },
  {
    q: '방은 언제 사라지나요?',
    a: '대기 중인 방은 1시간, 진행 중인 방은 마지막 활동 후 2시간, 끝난 방은 1시간 뒤 자동으로 삭제됩니다. 방장이 방을 종료하거나 모두 나가면 바로 삭제됩니다.',
  },
  {
    q: '비용이 드나요?',
    a: '무료입니다. 서비스 운영을 위해 일부 화면에 광고가 표시될 수 있습니다.',
  },
];

export function AboutPage() {
  useDocumentMeta({
    title: '서비스 소개',
    description: '오늘 뭐먹지?는 회원가입 없이 동료들과 링크 하나로 모여 무작위 순서로 점심 메뉴를 정하는 무료 서비스입니다. 사용법과 자주 묻는 질문을 확인하세요.',
    path: '/about',
  });
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };

  return (
    <article className="flex flex-col gap-8 animate-fade-up">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />

      <header>
        <p className="text-sm font-semibold text-primary-600">서비스 소개</p>
        <h1 className="mt-1 text-3xl font-extrabold">{BRAND.name}는 어떤 서비스인가요?</h1>
        <p className="mt-4 leading-relaxed text-charcoal-700">
          점심시간마다 반복되는 "오늘 뭐 먹지?"를 끝내기 위해 만들었습니다. 한 사람이 메뉴를 정하면 눈치가 보이고, 투표를 하면 늘 같은 메뉴가
          이기고, 랜덤 룰렛은 아무도 책임지지 않죠. {BRAND.name}는 <strong>여러 사람이 한 단계씩 돌아가며</strong> 정합니다. 누가 어떤 단계를
          맡을지는 무작위로 정해지고 미리 공개되지 않아서, 결과가 나올 때까지 모두가 같이 기다리게 됩니다.
        </p>
      </header>

      <section className="card p-5 sm:p-6">
        <h2 className="text-xl font-bold">이렇게 진행돼요</h2>
        <ol className="mt-4 flex flex-col gap-3 text-charcoal-700">
          <li className="flex gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-white">1</span>
            <div>
              <p className="font-semibold">방을 만듭니다</p>
              <p className="text-sm">참여 인원과 닉네임만 정하면 끝. 필요하면 비밀번호도 걸 수 있어요.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-white">2</span>
            <div>
              <p className="font-semibold">초대 링크를 공유합니다</p>
              <p className="text-sm">카카오톡, 슬랙, 어디든 링크 하나면 됩니다. 들어온 사람은 닉네임만 입력해요.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-white">3</span>
            <div>
              <p className="font-semibold">인원이 차면 자동으로 시작됩니다</p>
              <p className="text-sm">배달/외식 → 음식 종류 → 세부 종류 → 최종 메뉴. 매 단계 다른 사람이 고르고, 랜덤 버튼도 있어요.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-white">4</span>
            <div>
              <p className="font-semibold">오늘의 점심이 정해집니다</p>
              <p className="text-sm">누가 무엇을 골랐는지 공개되고, 결과를 공유하거나 같은 방에서 다시 정할 수 있어요.</p>
            </div>
          </li>
        </ol>
      </section>

      <section>
        <h2 className="text-xl font-bold">메뉴 데이터</h2>
        <p className="mt-2 leading-relaxed text-charcoal-700">
          한식, 중식, 일식, 양식, 분식, 아시안, 멕시칸/남미, 패스트푸드, 샐러드/건강식, 기타까지 10개 대분류 아래 {countMenus()}개의 메뉴가
          들어 있습니다. 한국 직장가 점심에서 실제로 자주 먹는 메뉴를 기준으로 골랐고, 계속 다듬고 있습니다.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-bold">자주 묻는 질문</h2>
        <dl className="mt-4 flex flex-col gap-3">
          {FAQ.map((f) => (
            <div key={f.q} className="card p-4">
              <dt className="font-semibold">{f.q}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-charcoal-700">{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="card p-5 text-center">
        <p className="font-semibold">지금 바로 써보세요</p>
        <p className="mt-1 text-sm text-charcoal-500">회원가입 없이 10초면 방이 만들어져요.</p>
        <Link to="/create" className="btn-primary btn-md mt-4">
          오늘 점심 정하기
          <ArrowRight size={18} aria-hidden="true" />
        </Link>
      </section>
    </article>
  );
}
